import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Globe, Check } from 'lucide-react';
import {
  COUNTRIES,
  getBusinessTypeLabelForCountry,
  getCountryById,
  getCountryTerminology
} from '../../lib/countrySettings';

const FLAG_MAP = {
  ie: 'IE',
  uk: 'UK',
  us: 'US'
};

const DESCRIPTION_MAP = {
  ie: 'Eurozone',
  uk: 'British Pound Sterling',
  us: 'US Dollar'
};

const CountrySelectionModal = ({
  isOpen,
  onClose,
  onCountrySelect,
  currentCountry,
  isNewUser = false,
  requireBusinessSetup = false,
  businessType = '',
  businessTypeOptions = [],
  industryId = '',
  industryOptions = [],
  serviceOfferingOptions = [],
  selectedServiceOfferings = [],
  vatRegistered = false,
  vatAccountingBasis = 'cash'
}) => {
  const getInitialCountry = useCallback(() => {
    const storedCountry = localStorage.getItem('selectedCountry');
    if (storedCountry) return storedCountry;
    if (requireBusinessSetup) return '';
    if (currentCountry?.id) return currentCountry.id;
    return '';
  }, [currentCountry?.id, requireBusinessSetup]);

  const getInitialBusinessType = useCallback(() => (
    businessType || ''
  ), [businessType]);

  const getInitialIndustry = useCallback(() => {
    if (industryId) return industryId;
    try {
      return localStorage.getItem('70livesbeautyledgerIndustry') || (requireBusinessSetup ? '' : 'beauty');
    } catch {
      return requireBusinessSetup ? '' : 'beauty';
    }
  }, [industryId, requireBusinessSetup]);

  const getInitialVatStatus = useCallback(() => (
    vatRegistered ? 'registered' : (requireBusinessSetup ? '' : 'not_registered')
  ), [requireBusinessSetup, vatRegistered]);
  const getInitialVatAccountingBasis = useCallback(() => (
    vatAccountingBasis === 'invoice' ? 'invoice' : 'cash'
  ), [vatAccountingBasis]);
  const getInitialServiceOfferings = useCallback(() => {
    if (Array.isArray(selectedServiceOfferings) && selectedServiceOfferings.length) {
      return selectedServiceOfferings;
    }
    try {
      const stored = localStorage.getItem('70livesbeautyledgerServiceOfferings');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, [selectedServiceOfferings]);

  const [selectedCountryId, setSelectedCountryId] = useState(getInitialCountry());
  const [selectedBusinessType, setSelectedBusinessType] = useState(getInitialBusinessType());
  const [selectedIndustryId, setSelectedIndustryId] = useState(getInitialIndustry());
  const [localServiceOfferings, setLocalServiceOfferings] = useState(getInitialServiceOfferings());
  const [selectedVatStatus, setSelectedVatStatus] = useState(getInitialVatStatus());
  const [selectedVatAccountingBasis, setSelectedVatAccountingBasis] = useState(getInitialVatAccountingBasis());

  const normCountries = useMemo(
    () => COUNTRIES.map((country) => ({
      ...country,
      flag: FLAG_MAP[country.id] || String(country.id || '').toUpperCase(),
      description: DESCRIPTION_MAP[country.id] || country.currency,
      group: 'Supported Countries'
    })),
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    setSelectedCountryId(getInitialCountry());
    setSelectedBusinessType(getInitialBusinessType());
    setSelectedIndustryId(getInitialIndustry());
    setLocalServiceOfferings(getInitialServiceOfferings());
    setSelectedVatStatus(getInitialVatStatus());
    setSelectedVatAccountingBasis(getInitialVatAccountingBasis());
  }, [isOpen, getInitialBusinessType, getInitialCountry, getInitialIndustry, getInitialServiceOfferings, getInitialVatStatus, getInitialVatAccountingBasis]);

  const handleCountrySelect = (countryId) => {
    setSelectedCountryId(countryId);
  };

  const handleConfirm = () => {
    if (!hasSelection) return;
    const selectedCountry = getCountryById(selectedCountryId);
    if (selectedCountry) {
      localStorage.setItem('selectedCountry', selectedCountry.id);
      window.dispatchEvent(new CustomEvent('countryChanged', { detail: { country: selectedCountry } }));
      onCountrySelect(selectedCountry, requireBusinessSetup ? {
        businessType: selectedBusinessType,
        industryId: selectedIndustryId,
        serviceOfferings: localServiceOfferings,
        vatRegistered: selectedVatStatus === 'registered',
        vatAccountingBasis: selectedVatStatus === 'registered' ? selectedVatAccountingBasis : 'cash'
      } : null);
      if (onClose) {
        onClose(true);
      }
    }
  };

  if (!isOpen) return null;

  const selectedCountry = selectedCountryId ? getCountryById(selectedCountryId) : null;
  const countryTerminology = getCountryTerminology(selectedCountry?.id || currentCountry?.id || 'ie');
  const taxName = countryTerminology.taxName;
  const requiresVatAccountingBasis = requireBusinessSetup && selectedVatStatus === 'registered';
  const requiresServiceOfferings = requireBusinessSetup && selectedIndustryId === 'beauty';
  const hasSelection = Boolean(selectedCountryId) &&
    (!requireBusinessSetup || (
      Boolean(selectedBusinessType) &&
      Boolean(selectedIndustryId) &&
      (!requiresServiceOfferings || localServiceOfferings.length > 0) &&
      Boolean(selectedVatStatus) &&
      (!requiresVatAccountingBasis || Boolean(selectedVatAccountingBasis))
    ));
  const availableBusinessTypeOptions = (businessTypeOptions.length
    ? businessTypeOptions
    : [
        { id: 'sole_trader' },
        { id: 'limited_company' }
      ]).map((option) => ({
        ...option,
        label: getBusinessTypeLabelForCountry(option.id, selectedCountry?.id || currentCountry?.id || 'ie')
      }));
  const availableIndustryOptions = industryOptions.length
    ? industryOptions
    : [
        { id: 'beauty', label: 'Beauty & Wellness', description: 'Salons, spas, hair, nails, brows, lashes.' }
      ];
  const availableServiceOfferingOptions = serviceOfferingOptions.length
    ? serviceOfferingOptions
    : [
        { id: 'hair', label: 'Hair' },
        { id: 'makeup_beauty', label: 'Makeup / Beauty' },
        { id: 'brows_lashes', label: 'Brows / Lashes' },
        { id: 'nails', label: 'Nails' },
        { id: 'aesthetics_dermatology', label: 'Aesthetics / Dermatology' },
        { id: 'massage_acupuncture', label: 'Massage / Acupuncture' },
        { id: 'other', label: 'Other' }
      ];
  const toggleServiceOffering = (offeringId) => {
    setLocalServiceOfferings((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      return current.includes(offeringId)
        ? current.filter((id) => id !== offeringId)
        : [...current, offeringId];
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Globe className="w-6 h-6 text-[color:var(--color-primary)]" />
            <div>
              <h2 className="text-xl font-semibold text-[color:var(--color-text)]">
                {requireBusinessSetup ? 'Complete Required Setup' : (isNewUser ? 'Welcome! Select Your Country' : 'Select Your Country')}
              </h2>
              {(isNewUser || requireBusinessSetup) && (
                <p className="text-sm text-[color:var(--color-text)] mt-1">
                  This is required for accurate currency, tax, and reporting calculations.
                </p>
              )}
            </div>
          </div>
          {!isNewUser && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>

        <div className="p-6">
          <p className="text-[color:var(--color-text)] mb-6 text-center">
            {requireBusinessSetup
              ? 'Confirm these details before continuing.'
              : 'Choose your country to set the correct currency and tax calculations for your business.'}
          </p>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-[color:var(--color-text)] mb-2 px-2">Supported Countries</h3>
              <div className="space-y-2">
                {normCountries.map((country) => (
                  <div
                    key={country.id}
                    onClick={() => handleCountrySelect(country.id)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition ${
                      selectedCountryId === country.id
                        ? 'border-[color:var(--color-primary)] bg-white'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{country.flag}</span>
                        <div>
                          <h4 className="font-medium text-[color:var(--color-text)]">{country.name}</h4>
                          <p className="text-xs text-[color:var(--color-text)]">{country.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-lg font-bold text-[color:var(--color-primary)]">
                              {country.currencySymbol}
                            </span>
                            <span className="text-sm text-gray-500">{country.currency}</span>
                          </div>
                        </div>
                      </div>
                      {selectedCountryId === country.id && (
                        <Check className="w-4 h-4 text-[color:var(--color-primary)]" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {requireBusinessSetup && (
              <>
                <div>
                  <h3 className="text-sm font-semibold text-[color:var(--color-text)] mb-2 px-2">Business Structure</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {availableBusinessTypeOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setSelectedBusinessType(option.id)}
                        className={`p-3 rounded-lg border-2 text-left transition ${
                          selectedBusinessType === option.id
                            ? 'border-[color:var(--color-primary)] bg-white'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium text-[color:var(--color-text)]">{option.label}</span>
                          {selectedBusinessType === option.id && (
                            <Check className="w-4 h-4 text-[color:var(--color-primary)]" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-[color:var(--color-text)] mb-2 px-2">Industry</h3>
                  <div className="grid grid-cols-1 gap-2">
                    {availableIndustryOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          setSelectedIndustryId(option.id);
                          if (option.id !== 'beauty') {
                            setLocalServiceOfferings([]);
                          }
                        }}
                        className={`p-3 rounded-lg border-2 text-left transition ${
                          selectedIndustryId === option.id
                            ? 'border-[color:var(--color-primary)] bg-white'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <span>
                            <span className="block font-medium text-[color:var(--color-text)]">{option.label}</span>
                            {option.description && (
                              <span className="mt-1 block text-xs text-gray-500">{option.description}</span>
                            )}
                          </span>
                          {selectedIndustryId === option.id && (
                            <Check className="mt-0.5 w-4 h-4 flex-shrink-0 text-[color:var(--color-primary)]" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {selectedIndustryId === 'beauty' && (
                  <div>
                    <h3 className="text-sm font-semibold text-[color:var(--color-text)] mb-2 px-2">Services Provided</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {availableServiceOfferingOptions.map((option) => {
                        const checked = localServiceOfferings.includes(option.id);
                        return (
                          <label
                            key={option.id}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition flex items-center justify-between gap-3 ${
                              checked
                                ? 'border-[color:var(--color-primary)] bg-white'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <span className="font-medium text-[color:var(--color-text)]">{option.label}</span>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleServiceOffering(option.id)}
                              className="h-4 w-4"
                            />
                          </label>
                        );
                      })}
                    </div>
                    <p className="mt-2 px-2 text-xs text-gray-500">
                      These choices keep sales and expense categories focused on your services.
                    </p>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-semibold text-[color:var(--color-text)] mb-2 px-2">{taxName} Registration</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { id: 'registered', label: `Registered for ${taxName}` },
                      { id: 'not_registered', label: 'Not registered yet' }
                    ].map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setSelectedVatStatus(option.id)}
                        className={`p-3 rounded-lg border-2 text-left transition ${
                          selectedVatStatus === option.id
                            ? 'border-[color:var(--color-primary)] bg-white'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium text-[color:var(--color-text)]">{option.label}</span>
                          {selectedVatStatus === option.id && (
                            <Check className="w-4 h-4 text-[color:var(--color-primary)]" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {selectedVatStatus === 'registered' && (
                  <div>
                    <h3 className="text-sm font-semibold text-[color:var(--color-text)] mb-2 px-2">{taxName} Accounting Basis</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        {
                          id: 'cash',
                          label: 'Cash basis',
                          description: `${taxName} is reported when money is paid or received.`
                        },
                        {
                          id: 'invoice',
                          label: 'Invoice basis',
                          description: `${taxName} is reported by invoice or receipt date.`
                        }
                      ].map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setSelectedVatAccountingBasis(option.id)}
                          className={`p-3 rounded-lg border-2 text-left transition ${
                            selectedVatAccountingBasis === option.id
                              ? 'border-[color:var(--color-primary)] bg-white'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <span>
                              <span className="block font-medium text-[color:var(--color-text)]">{option.label}</span>
                              <span className="mt-1 block text-xs text-gray-500">{option.description}</span>
                            </span>
                            {selectedVatAccountingBasis === option.id && (
                              <Check className="mt-0.5 w-4 h-4 flex-shrink-0 text-[color:var(--color-primary)]" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            {!isNewUser && (
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-[color:var(--color-text)] text-[color:var(--color-text)] rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={!hasSelection}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition ${
                hasSelection
                  ? 'bg-[color:var(--color-primary)] text-[color:var(--color-text)] hover:bg-[color:var(--color-primary-700)]'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {requireBusinessSetup ? 'Complete Setup' : 'Confirm Selection'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountrySelectionModal;
